import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, List, ListItem, ListItemText, Collapse } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { SidebarMenu } from "./sidebarMenu";
import { FiLogOut } from "react-icons/fi";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { AdminRole } from "../../enums/admin-roles";

type MenuNode = {
  menu: string;
  path?: string;
  role?: string;
  subMenu?: MenuNode[];
};

/** Matches exact path or nested routes (e.g. /examination/questions/...); `/` only matches the dashboard root. */
const pathMatches = (pathname: string, menuPath: string): boolean => {
  const p = pathname.replace(/\/$/, "") || "/";
  const m = menuPath.replace(/\/$/, "") || "/";
  if (m === "/") return p === "/" || p === "";
  return p === m || p.startsWith(`${m}/`);
};

/** True if this node or any descendant route matches the current URL. */
const subtreeHasActivePath = (node: MenuNode, pathname: string): boolean => {
  if (node.path && pathMatches(pathname, node.path)) return true;
  if (node.subMenu) {
    return node.subMenu.some((child) => subtreeHasActivePath(child, pathname));
  }
  return false;
};

/** Marks every ancestor menu key so nested items stay visible after navigation or direct URL load. */
const collectOpenKeys = (node: MenuNode, pathname: string): Record<string, boolean> => {
  const open: Record<string, boolean> = {};

  const walk = (n: MenuNode): boolean => {
    if (n.path && pathMatches(pathname, n.path)) return true;
    if (n.subMenu) {
      for (const child of n.subMenu) {
        if (walk(child)) {
          open[n.menu] = true;
          return true;
        }
      }
    }
    return false;
  };

  walk(node);
  return open;
};

const buildAutoOpenMenus = (pathname: string): Record<string, boolean> => {
  const merged: Record<string, boolean> = {};
  for (const item of SidebarMenu as MenuNode[]) {
    Object.assign(merged, collectOpenKeys(item, pathname));
  }
  return merged;
};

/** Most specific sidebar link for the URL (longest matching path) — used for scroll target. */
const findLongestMatchingMenuNode = (
  items: MenuNode[],
  pathname: string
): MenuNode | null => {
  let best: MenuNode | null = null;
  let bestLen = -1;
  const walk = (nodes: MenuNode[]) => {
    for (const n of nodes) {
      if (n.path && pathMatches(pathname, n.path)) {
        if (n.path.length > bestLen) {
          bestLen = n.path.length;
          best = n;
        }
      }
      if (n.subMenu) walk(n.subMenu);
    }
  };
  walk(items);
  return best;
};

const sameScrollNode = (
  a: MenuNode | null,
  b: { menu: string; path?: string }
): boolean =>
  !!a && a.menu === b.menu && (a.path ?? "") === (b.path ?? "");

const Sidebar: React.FC = () => {
  const location = useLocation(); // Get current route location
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>(() =>
    buildAutoOpenMenus(location.pathname)
  );
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  const autoOpenMenus = useMemo(
    () => buildAutoOpenMenus(location.pathname),
    [location.pathname]
  );

  const scrollTarget = useMemo(
    () => findLongestMatchingMenuNode(SidebarMenu as MenuNode[], location.pathname),
    [location.pathname]
  );

  const activeMenuItemRef = useRef<HTMLElement | null>(null);

  const setActiveMenuRef = useCallback((el: HTMLElement | null) => {
    activeMenuItemRef.current = el;
  }, []);

  useEffect(() => {
    setOpenMenus((prev) => ({ ...prev, ...autoOpenMenus }));
  }, [autoOpenMenus]);

  /** Scroll after submenu collapse/expand so the active row is in view. */
  useEffect(() => {
    if (!scrollTarget) return;
    const t = window.setTimeout(() => {
      activeMenuItemRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }, 420);
    return () => window.clearTimeout(t);
  }, [location.pathname, scrollTarget?.path, scrollTarget?.menu]);

  const toggleMenu = (menu: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const isActive = (path: string | undefined): boolean =>
    !!path && pathMatches(location.pathname, path);

  const isGroupActive = (node: MenuNode): boolean =>
    subtreeHasActivePath(node, location.pathname);

  const handleLogout = () => {
    // Implement your logout logic here, like clearing tokens or redirecting
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div className="sidebar">
      <Drawer
        sx={{
          width: 240,
          flexShrink: 0,
          "& .MuiListItem-gutters ": {
            transition: "background-color 0.3s",
          },
          "& .MuiDrawer-paper": {
            width: 240,
            boxSizing: "border-box",
            backgroundColor: "#171717",
            borderRight: "1px solid var(--color-primary)",
            height: "100vh",
            maxHeight: "100vh",
            overflowY: "auto",
            overflowX: "hidden",
          },
        }}
        variant="permanent"
        className="drawer flex flex-column"
        open
      >
        <List>
          {SidebarMenu.map((item) => {
            const isAdmin = signedAdmin.role === AdminRole.ADMIN;
            const isFranchise = signedAdmin.type === "franchise";
            if (
              signedAdmin.role !== "super-admin" &&
              item.role === "super-admin"
            )
              return null;
            else if (
              isAdmin &&
              isFranchise &&
              (item.path === "/group-class" ||
                item.path === "/" ||
                item.path === "/access-details" ||
                item.path === "/teachers-shadow-wallet" || item.path === "/chat")
            ) {
              return null;
            } else {
              const topActive = item.subMenu
                ? isGroupActive(item as MenuNode)
                : isActive(item.path);
              const topScrollTarget =
                scrollTarget && sameScrollNode(scrollTarget, item);
              return (
                <React.Fragment key={item.menu}>
                  <ListItem
                    ref={topScrollTarget ? setActiveMenuRef : undefined}
                    component={item.path ? Link : "div"}
                    to={
                      signedAdmin.role !== "super-admin" &&
                      item.role === "super-admin"
                        ? null
                        : item.path || undefined
                    }
                    onClick={() => {
                      if (
                        signedAdmin.role !== "super-admin" &&
                        item.role === "super-admin"
                      )
                        return;
                      if (item?.subMenu) toggleMenu(item.menu);
                    }}
                    sx={{
                      backgroundColor: topActive
                        ? "var(--color-dark-gray)" // Active menu background color
                        : "var(--color-dark)", // Default background color
                      color: topActive
                        ? "var(--color-primary)" // Active menu text color
                        : "var(--color-light-gray)", // Default text color
                      "&:hover": {
                        backgroundColor: "var(--color-gray)", // Hover background color
                        color: "var(--color-primary)", // Hover text color
                      },
                      cursor:
                        signedAdmin.role !== "super-admin" &&
                        item.role === "super-admin"
                          ? "not-allowed"
                          : "",
                    }}
                  >
                    <ListItemText primary={item.menu} />
                    {item.subMenu && (
                      <>
                        {openMenus[item.menu] ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </>
                    )}
                  </ListItem>

                  {/* Handle Submenu */}
                  {item.subMenu && (
                    <Collapse
                      in={openMenus[item.menu]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {item.subMenu.map((subItem) => {
                          if (
                            isAdmin &&
                            isFranchise &&
                            (subItem.path === "/group-class-payments" ||
                              subItem.path === "/franchise-payments")
                          ) {
                            return null;
                          }
                          const subActive = subItem.subMenu
                            ? isGroupActive(subItem as MenuNode)
                            : isActive(subItem.path);
                          const subScrollTarget =
                            scrollTarget &&
                            sameScrollNode(scrollTarget, subItem);
                          return (
                            <React.Fragment key={subItem.menu}>
                              <ListItem
                                ref={subScrollTarget ? setActiveMenuRef : undefined}
                                component={subItem.path ? Link : "div"}
                                to={
                                  signedAdmin.role !== "super-admin" &&
                                  subItem.role === "super-admin"
                                    ? null
                                    : subItem.path || undefined
                                }
                                onClick={() => {
                                  if (
                                    signedAdmin.role !== "super-admin" &&
                                    subItem.role === "super-admin"
                                  )
                                    return;
                                  if (subItem.subMenu) toggleMenu(subItem.menu); // Toggle third-level menu
                                }}
                                sx={{
                                  pl: 4,
                                  backgroundColor: subActive
                                    ? "var(--color-dark-gray)" // Active sub-menu background color
                                    : "transparent", // Default sub-menu background color
                                  color: subActive
                                    ? "var(--color-primary)" // Active sub-menu text color
                                    : "var(--color-light-gray)", // Default sub-menu text color
                                  "&:hover": {
                                    backgroundColor: "var(--color-gray)", // Hover background color
                                    color: "var(--color-primary)", // Hover text color
                                  },
                                  cursor:
                                    signedAdmin.role !== "super-admin" &&
                                    subItem.role === "super-admin"
                                      ? "not-allowed"
                                      : "",
                                }}
                              >
                                <ListItemText primary={subItem.menu} />
                                {subItem.subMenu && (
                                  <>
                                    {openMenus[subItem.menu] ? (
                                      <FaChevronUp />
                                    ) : (
                                      <FaChevronDown />
                                    )}
                                  </>
                                )}
                              </ListItem>

                              {/* Handle Third-level Submenu (Submenu under Submenu) */}
                              {subItem.subMenu && (
                                <Collapse
                                  in={openMenus[subItem.menu]}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <List component="div" disablePadding>
                                    {subItem.subMenu.map((subSubItem) => (
                                      <ListItem
                                        key={subSubItem.menu}
                                        ref={
                                          scrollTarget &&
                                          sameScrollNode(scrollTarget, subSubItem)
                                            ? setActiveMenuRef
                                            : undefined
                                        }
                                        component={Link}
                                        to={subSubItem.path}
                                        sx={{
                                          pl: 6,
                                          backgroundColor: isActive(
                                            subSubItem.path
                                          )
                                            ? "var(--color-dark-gray)" // Active sub-sub-menu background color
                                            : "transparent", // Default background color
                                          color: isActive(subSubItem.path)
                                            ? "var(--color-primary)" // Active sub-sub-menu text color
                                            : "var(--color-light-gray)", // Default text color
                                          "&:hover": {
                                            backgroundColor:
                                              "var(--color-gray)", // Hover background color
                                            color: "var(--color-primary)", // Hover text color
                                          },
                                        }}
                                      >
                                        <ListItemText
                                          primary={subSubItem.menu}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Collapse>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            }
          })}
        </List>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          style={{ width: "calc(100% - 32px)" }}
          className="mt-auto mx-auto mb-4 flex items-center justify-center px-4 py-3 bg-red-600 text-white text-sm font-medium shadow-md transition-all duration-300 ease-in-out hover:bg-yellow-500 hover:text-black hover:shadow-lg active:scale-95 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
        >
          <FiLogOut className="mr-2 h-5 w-5" />
          Logout
        </button>
      </Drawer>
    </div>
  );
};

export default Sidebar;
